"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { getSuggest, type SuggestArticle } from "@/lib/api-client-browser";
import { postSearchLog } from "@/lib/search-log";

interface SuggestDropdownProps {
  /** 検索入力値 */
  inputValue: string;
  /** キーワード候補選択時のコールバック */
  onSelect: (keyword: string) => void;
  /** ドロップダウンを閉じるコールバック */
  onClose: () => void;
  /** 表示状態 */
  isOpen: boolean;
  /** モバイル表示かどうか */
  isMobile?: boolean;
  /** listbox の ID（親から aria-controls で参照するため） */
  listboxId: string;
  /** 外側クリック判定から除外するアンカー要素（検索入力フォームの親要素等） */
  anchorRef?: React.RefObject<HTMLElement | null>;
}

/**
 * テキスト内のハイライト箇所に mark タグを挿入する（XSS対策: エスケープ後に挿入）
 */
function renderHighlightedText(
  text: string,
  highlights: { start: number; length: number }[],
): React.ReactNode {
  if (highlights.length === 0) {
    return text;
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  // start でソートしたうえで重複/重なりを除去
  const sorted = [...highlights].sort((a, b) => a.start - b.start);

  for (const hl of sorted) {
    if (hl.start < lastIndex) continue;
    const end = hl.start + hl.length;
    if (hl.start > lastIndex) {
      parts.push(text.slice(lastIndex, hl.start));
    }
    parts.push(
      <mark key={hl.start} className="bg-yellow-100 text-inherit font-semibold not-italic">
        {text.slice(hl.start, end)}
      </mark>,
    );
    lastIndex = end;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
}

/**
 * サジェストドロップダウンコンポーネント
 * 検索バーの入力に応じてキーワード候補・記事候補を2セクションで表示
 */
export function SuggestDropdown({
  inputValue,
  onSelect,
  onClose,
  isOpen,
  isMobile = false,
  listboxId,
  anchorRef,
}: SuggestDropdownProps) {
  const router = useRouter();
  const [keywords, setKeywords] = useState<string[]>([]);
  const [articles, setArticles] = useState<SuggestArticle[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const keywordLimit = isMobile ? 3 : 5;
  const articleLimit = isMobile ? 2 : 3;

  // サジェスト取得（debounce 150ms）
  const fetchSuggestions = useCallback(
    async (prefix: string) => {
      if (!prefix.trim()) {
        setKeywords([]);
        setArticles([]);
        return;
      }

      setIsLoading(true);
      try {
        const result = await getSuggest(prefix.trim(), keywordLimit, articleLimit);
        setKeywords(result.keywords.slice(0, keywordLimit));
        setArticles(result.articles.slice(0, articleLimit));
        setSelectedIndex(-1);
      } catch (error) {
        console.error("サジェスト取得エラー:", error);
        setKeywords([]);
        setArticles([]);
      } finally {
        setIsLoading(false);
      }
    },
    [keywordLimit, articleLimit],
  );

  // 入力値変更時に debounce でサジェスト取得（150ms）
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!inputValue.trim()) {
      setKeywords([]);
      setArticles([]);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      void fetchSuggestions(inputValue);
    }, 150);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, fetchSuggestions]);

  // 全候補の合計数（キーワード + 記事 + フッター）
  const totalItems = keywords.length + articles.length + (inputValue.trim() ? 1 : 0);

  // キーボード操作
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
          break;
        case "Enter": {
          // 候補が未選択（selectedIndex < 0）の場合は form の submit に委譲する
          if (selectedIndex < 0) break;
          e.preventDefault();

          if (selectedIndex < keywords.length) {
            // キーワード候補を選択
            const keyword = keywords[selectedIndex];
            if (keyword) onSelect(keyword);
          } else if (selectedIndex < keywords.length + articles.length) {
            // 記事候補を選択
            const article = articles[selectedIndex - keywords.length];
            if (article) {
              void postSearchLog({
                query: inputValue,
                resultCount: null,
                source: "suggest",
              }).catch(console.error);
              router.push(`/articles/${article.slug}`);
              onClose();
            }
          } else {
            // フッター「"xxx" で検索」を選択
            onSelect(inputValue.trim());
          }
          break;
        }
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    isOpen,
    keywords,
    articles,
    selectedIndex,
    totalItems,
    onSelect,
    onClose,
    inputValue,
    router,
  ]);

  // キーボード選択時のスクロール追従
  useEffect(() => {
    if (selectedIndex < 0) return;
    const itemId = `${listboxId}-item-${selectedIndex}`;
    const el = document.getElementById(itemId);
    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, listboxId]);

  // 外側クリックで閉じる
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (!(e.target instanceof Node)) {
        onClose();
        return;
      }
      const isInsideDropdown = dropdownRef.current?.contains(e.target) ?? false;
      const isInsideAnchor = anchorRef?.current?.contains(e.target) ?? false;
      if (!isInsideDropdown && !isInsideAnchor) {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  const hasResults = keywords.length > 0 || articles.length > 0;

  if (!isOpen) {
    return null;
  }

  // ドロップダウンの ID 生成ヘルパー
  const getItemId = (index: number) => `${listboxId}-item-${index}`;
  const activeDescendant = selectedIndex >= 0 ? getItemId(selectedIndex) : undefined;

  return (
    <div
      ref={dropdownRef}
      id={listboxId}
      role="listbox"
      aria-label="検索候補"
      aria-activedescendant={activeDescendant}
      className="absolute top-full left-0 right-0 z-50 mt-1 max-h-[400px] overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg"
    >
      {isLoading ? (
        <div className="px-4 py-3 text-sm text-neutral-400">読み込み中...</div>
      ) : !hasResults && inputValue.trim() ? (
        // ゼロ結果UI（T4で詳細実装）
        <div className="px-4 py-4 text-sm text-neutral-500">
          <p className="font-medium">&ldquo;{inputValue}&rdquo; に一致する結果はありませんでした</p>
          <p className="mt-1 text-neutral-400">キーワードを変えて再検索してください</p>
        </div>
      ) : (
        <>
          {/* キーワード候補セクション */}
          {keywords.length > 0 && (
            <div role="group" aria-label="キーワード候補">
              <div className="border-b border-neutral-100 px-4 py-1.5 text-xs font-semibold text-neutral-400">
                キーワード候補
              </div>
              <ul>
                {keywords.map((keyword, index) => (
                  <li
                    key={keyword}
                    id={getItemId(index)}
                    role="option"
                    aria-selected={index === selectedIndex}
                    className={`flex min-h-[44px] cursor-pointer items-center gap-2 px-4 py-2 text-sm ${
                      index === selectedIndex
                        ? "bg-[var(--theme-primary,#0066CC)] text-white"
                        : "text-neutral-700 hover:bg-neutral-100"
                    }`}
                    onClick={() => onSelect(keyword)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <svg
                      className="h-4 w-4 shrink-0 opacity-50"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <span>{renderHighlightedText(keyword, [])}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 記事候補セクション */}
          {articles.length > 0 && (
            <div role="group" aria-label="記事">
              <div className="border-b border-neutral-100 px-4 py-1.5 text-xs font-semibold text-neutral-400">
                記事
              </div>
              <ul>
                {articles.map((article, index) => {
                  const itemIndex = keywords.length + index;
                  const isSelected = itemIndex === selectedIndex;
                  const snippetText = isMobile
                    ? (article.snippet?.slice(0, 60) ?? null)
                    : article.snippet;

                  return (
                    <li
                      key={article.id}
                      id={getItemId(itemIndex)}
                      role="option"
                      aria-selected={isSelected}
                      className={`flex min-h-[44px] cursor-pointer items-start gap-2 px-4 py-3 text-left text-sm ${
                        isSelected
                          ? "bg-[var(--theme-primary,#0066CC)] text-white"
                          : "text-neutral-700 hover:bg-neutral-100"
                      }`}
                      onClick={() => {
                        void postSearchLog({
                          query: inputValue,
                          resultCount: null,
                          source: "suggest",
                        }).catch(console.error);
                        router.push(`/articles/${article.slug}`);
                        onClose();
                      }}
                      onMouseEnter={() => setSelectedIndex(itemIndex)}
                    >
                      <svg
                        className="mt-0.5 h-4 w-4 shrink-0 opacity-50"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium leading-snug">
                          {renderHighlightedText(article.title, article.titleHighlights)}
                        </p>
                        {snippetText && (
                          <p
                            className={`mt-0.5 text-xs leading-relaxed ${
                              isSelected ? "text-white/80" : "text-neutral-500"
                            }`}
                          >
                            {renderHighlightedText(snippetText, article.snippetHighlights)}
                          </p>
                        )}
                        {article.category && (
                          <span
                            className={`mt-1 inline-block rounded px-1.5 py-0.5 text-xs ${
                              isSelected
                                ? "bg-white/20 text-white"
                                : "bg-neutral-100 text-neutral-500"
                            }`}
                          >
                            {article.category.name}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* フッター: "[入力テキスト]" で検索 */}
          {inputValue.trim() && (
            <div className="border-t border-neutral-100">
              <button
                id={getItemId(keywords.length + articles.length)}
                role="option"
                aria-selected={selectedIndex === keywords.length + articles.length}
                type="button"
                className={`flex min-h-[44px] w-full cursor-pointer items-center gap-2 px-4 py-2 text-sm ${
                  selectedIndex === keywords.length + articles.length
                    ? "bg-[var(--theme-primary,#0066CC)] text-white"
                    : "text-[var(--theme-primary,#0066CC)] hover:bg-neutral-50"
                }`}
                onClick={() => onSelect(inputValue.trim())}
                onMouseEnter={() => setSelectedIndex(keywords.length + articles.length)}
              >
                <svg
                  className="h-4 w-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <span>&ldquo;{inputValue}&rdquo; で検索</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
